import { IModel, Collection, IViolation } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';

import { DataFlowColumn } from '../dataset/dataflowcolumn';
import { ExpressionBasedColumn } from './expression-based.column';
import { NamedArray } from '../base/namedarray';
import { BasePivotTransformation } from './base-pivottransformation';
import { format, sanitizeAndFormatString } from 'app/shared/utils/string.util';
import { Owned } from '../base/named';

@Metamodel.model({
    name: 'unpivot',
    display: [stringResources.DataFlowUnpivotTransformationLabel, stringResources.DataFlowUnpivotTransformationsLabel],
    referable: true,
    constraints: {
        name: {
            required: true
        },
        unpivotColumns: {
            validator: UnpivotTransformation.validateUnpivotColumns
        }
    }
})
export class UnpivotTransformation extends BasePivotTransformation {
    public static readonly MODEL: IModel;
    public transformationLabel = stringResources.UnpivotTransformationUILabel;

    @Metamodel.attribute({
        type: DataFlowColumn.MODEL,
        constraint: {
            required: true,
            validator: UnpivotTransformation.validatePivotKeyColumn
        }
    })
    public unpivotKeyColumn: DataFlowColumn = Owned.create<DataFlowColumn>(DataFlowColumn.MODEL, this);

    @Metamodel.attribute({
        type: DataFlowColumn.MODEL,
        collection: Collection.Array,
        constraint: {
            unique: true,
            minOccurs: 1,
            validator: UnpivotTransformation.validateUnpivotedColumns
        },
        display: stringResources.UnpivotTransformationPivotedColumns
    })
    public unpivotedColumns = NamedArray.create<DataFlowColumn>(this);

    public lateralColumnArrangement: boolean = true;
    public dropNullRows: boolean = false;
    public pickColumnsAsValues: boolean = true;

    public get pivotColumns(): ExpressionBasedColumn[] {
        this._pivotColumns = [].concat(this.unpivotKeyColumn, this.unpivotedColumns);
        return this._pivotColumns;
    }

    public get unGroupByColumns(): ExpressionBasedColumn[] {
        return this.groupByColumns;
    }

    public static validateUnpivotColumns(transformation: UnpivotTransformation): IViolation[] | undefined {
        const groupedColumns = transformation.inputColumns
            .filter((column) => transformation.unGroupByColumns.map((gc) => gc.column.name).indexOf(column.name) < 0);
        let values = groupedColumns.map((column) => column.name);

        if (transformation.pivotKeyValues.length && !transformation.pickColumnsAsValues) {
            values = transformation.pivotKeyValues;
            const valueLength = transformation.pivotKeyValues.length;
            const unpivotColumnsLength = transformation.unpivotedColumns.length;
            if (valueLength * unpivotColumnsLength < groupedColumns.length) {
                return [
                    {
                        context: transformation,
                        message: () => format(stringResources.UnpivotTransformationUPIV012, (groupedColumns.length - valueLength * unpivotColumnsLength).toString())
                    }
                ];
            }
        }

        if (transformation.lateralColumnArrangement) {
            const unMatchedColumn = groupedColumns.find((groupColumn, index) => {
                return transformation.unpivotedColumns[index % transformation.unpivotedColumns.length] ?
                    groupColumn.dataFlowColumnType.fullyQualifiedType !== transformation.unpivotedColumns[index % transformation.unpivotedColumns.length].dataFlowColumnType.fullyQualifiedType
                    : false;
            });

            if (unMatchedColumn) {
                return [
                    {
                        context: transformation,
                        message: () => sanitizeAndFormatString(stringResources.UnpivotTransformationUPIV08, unMatchedColumn.name)
                    }
                ];
            }
        } else {
            const unMatchedColumn = groupedColumns.find((groupColumn, index) => {
                return transformation.unpivotedColumns[Math.floor(index / values.length)] ?
                    groupColumn.dataFlowColumnType.fullyQualifiedType !== transformation.unpivotedColumns[Math.floor(index / values.length)].dataFlowColumnType.fullyQualifiedType
                    : false;
            });

            if (unMatchedColumn) {
                return [
                    {
                        context: transformation,
                        message: () => sanitizeAndFormatString(stringResources.UnpivotTransformationUPIV08, unMatchedColumn.name)
                    }
                ];
            }
        }

        return undefined;
    }

    private static validatePivotKeyColumn(transformation: UnpivotTransformation, value: DataFlowColumn): IViolation[] | undefined {
        if (!transformation.unpivotKeyColumn || !transformation.unpivotKeyColumn.name) {
            return [
                {
                    context: transformation,
                    message: () => format(stringResources.PivotTransformationPivotKeyColumnRequired, transformation.transformationLabel)
                }
            ];
        }
        return undefined;
    }

    private static validateUnpivotedColumns(transformation: UnpivotTransformation, value: DataFlowColumn[]): IViolation[] | undefined {
        const columnSet: Set<string> = new Set<string>();
        const duplicateSet: Set<string> = new Set<string>();
        transformation.unGroupByColumns.forEach((unGroupColumn) => columnSet.add(unGroupColumn.column.name));
        transformation.unpivotedColumns.forEach((unpivotColumn) => {
            if (columnSet.has(unpivotColumn.name)) {
                duplicateSet.add(unpivotColumn.name);
            } else {
                columnSet.add(unpivotColumn.name);
            }
        });
        return duplicateSet.size > 0 ? [
            {
                context: transformation,
                message: () => sanitizeAndFormatString(stringResources.UnpivotTransformationUPIV011, [...duplicateSet].join(','))
            }
        ] : undefined;
    }

    protected getGeneratedDescription(): string {
        return stringResources.UnpivotTransformationDescription;
    }
}
